import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, formatDate, STATUS_TONE } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import { Plus, Search, FileText, Download, Upload as UploadIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

const EMPTY_NEW = {
  invoice_number: "", fy: "", party_client_name: "", project: "", subvendor_name: "",
  rc_number: "", rc_amount: 0, rc_start_date: "", rc_end_date: "",
  ro_no: "", ro_date: "", invoice_date: "", invoice_description: "",
  invoice_service_period: "", base_amount: 0, gst_amount: 0, net_amount: 0,
};

export default function Invoices() {
  const { hasPerm } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState({ fy: [], project: [], subvendor: [], party_client_name: [], status: [] });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fyFilter, setFyFilter] = useState("all");
  const [partyFilter, setPartyFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sesPendingOnly, setSesPendingOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState("all");
  const [exportParty, setExportParty] = useState("all");
  const [exportCount, setExportCount] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [projectList, setProjectList] = useState([]);

  // Bulk Status Update state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDryRun, setBulkDryRun] = useState(true);

  const downloadStatusTemplate = async () => {
    try {
      const res = await api.get("/invoice-status-bulk/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_status_bulk_template_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const runBulkUpload = async () => {
    if (!bulkFile) return toast.error("Please choose an Excel file");
    try {
      setBulkBusy(true);
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post(`/invoice-status-bulk/upload?dry_run=${bulkDryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      if (!bulkDryRun) {
        if (data.updated_count > 0) {
          toast.success(`${data.updated_count} invoice(s) updated`);
          load();
        } else if (data.error_count > 0) {
          toast.error(`No updates · ${data.error_count} error(s)`);
        } else {
          toast.info("No changes detected");
        }
      } else {
        toast.success(`Preview ready · ${data.updated_count} will update, ${data.error_count} errors`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Apply changes directly after viewing a dry-run preview (no need to toggle the checkbox)
  const applyAfterPreview = async () => {
    if (!bulkFile) return toast.error("File missing — please re-choose");
    if (!window.confirm(`Apply ${bulkResult?.updated_count || 0} status update(s)? This cannot be undone.`)) return;
    try {
      setBulkBusy(true);
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post(`/invoice-status-bulk/upload?dry_run=false`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      setBulkDryRun(false);
      if (data.updated_count > 0) {
        toast.success(`${data.updated_count} invoice(s) updated`);
        load();
      } else {
        toast.info("Nothing applied — please re-check the file");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Apply URL search params on mount (Dashboard drill-through)
  useEffect(() => {
    const sp = searchParams;
    if (sp.get("status")) setStatusFilter(sp.get("status"));
    if (sp.get("fy")) setFyFilter(sp.get("fy"));
    if (sp.get("party_client_name")) setPartyFilter(sp.get("party_client_name"));
    if (sp.get("project")) setProjectFilter(sp.get("project"));
    if (sp.get("outstanding") === "1") setOutstandingOnly(true);
    if (sp.get("overdue") === "1") setOverdueOnly(true);
    if (sp.get("ses_pending") === "1") setSesPendingOnly(true);
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (statusFilter && statusFilter !== "all") params.status = statusFilter;
    if (fyFilter && fyFilter !== "all") params.fy = fyFilter;
    if (partyFilter && partyFilter !== "all") params.party_client_name = partyFilter;
    if (projectFilter && projectFilter !== "all") params.project = projectFilter;
    const { data } = await api.get("/invoices", { params });
    setInvoices(data);
  };

  useEffect(() => {
    api.get("/invoices/filters").then(({ data }) => setFilters(data));
    api.get("/projects", { params: { active_only: true } }).then(({ data }) => setProjectList(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, fyFilter, partyFilter, projectFilter]);

  // Debounced auto-search as user types in the search box
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const onSearchKey = (e) => {
    if (e.key === "Enter") load();
  };

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitNew = async () => {
    try {
      await api.post("/invoices", {
        ...form,
        rc_amount: Number(form.rc_amount || 0),
        base_amount: Number(form.base_amount || 0),
        gst_amount: Number(form.gst_amount || 0),
        net_amount: Number(form.net_amount || (Number(form.base_amount || 0) + Number(form.gst_amount || 0))),
      });
      toast.success("Invoice created");
      setOpen(false);
      setForm(EMPTY_NEW);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const displayedInvoices = useMemo(() => {
    let rows = invoices;
    if (outstandingOnly) rows = rows.filter((i) => (i.outstanding || 0) > 0.005);
    if (overdueOnly) rows = rows.filter((i) => i.payment_due_date && i.payment_due_date < todayIso && (i.outstanding || 0) > 0.005);
    if (sesPendingOnly) rows = rows.filter((i) => !i.jobsheet?.ses_approval_date);
    return rows;
  }, [invoices, outstandingOnly, overdueOnly, sesPendingOnly, todayIso]);

  const {
    pageItems: pagedInvoices,
    page: invPage, setPage: setInvPage,
    pageSize: invPageSize, setPageSize: setInvPageSize,
    totalPages: invTotalPages, totalItems: invTotalItems,
  } = usePagination(displayedInvoices, "voltledger.invoices.pageSize");

  const totals = useMemo(() => {
    return {
      count: displayedInvoices.length,
      outstanding: displayedInvoices.reduce((s, i) => s + (i.outstanding || 0), 0),
      net_payable: displayedInvoices.reduce((s, i) => s + (i.net_payable || 0), 0),
    };
  }, [displayedInvoices]);

  // Preview count when export dialog open or selection changes
  useEffect(() => {
    if (!exportOpen) return;
    const params = {};
    if (exportStatus && exportStatus !== "all") params.status = exportStatus;
    if (exportParty && exportParty !== "all") params.party_client_name = exportParty;
    setExportCount(null);
    api.get("/exports/invoices-status/count", { params })
      .then(({ data }) => setExportCount(data))
      .catch(() => setExportCount({ count: 0, max: 1000 }));
  }, [exportOpen, exportStatus, exportParty]);

  const exportExcel = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (exportStatus && exportStatus !== "all") params.status = exportStatus;
      if (exportParty && exportParty !== "all") params.party_client_name = exportParty;
      const res = await api.get("/exports/invoices-status", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sSuf = (exportStatus && exportStatus !== "all") ? exportStatus.replace(/\s+/g, "_").replace(/\//g, "-") : "All";
      const pSuf = (exportParty && exportParty !== "all") ? "_" + exportParty.replace(/\s+/g, "_") : "";
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `invoice_status_${sSuf}${pSuf}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      setExportOpen(false);
    } catch (e) {
      // Parse blob error body (axios returns blob even for error)
      let msg = "Export failed";
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch (_) {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1600px]">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Records</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Invoices</h1>
          <p className="text-sm text-slate-500 mt-2">
            {totals.count} invoices · Net Payable {formatINR(totals.net_payable)} · Outstanding {formatINR(totals.outstanding)}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {hasPerm("invoices", "edit") && (
            <>
              <Button
                onClick={downloadStatusTemplate}
                variant="outline"
                className="rounded-none h-10 border-slate-300"
                data-testid="bulk-status-template-btn"
                title="Download Excel template prefilled with your invoices' current status"
              >
                <Download className="w-4 h-4 mr-1.5" /> Status Template
              </Button>
              <Button
                onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkFile(null); setBulkDryRun(true); }}
                variant="outline"
                className="rounded-none h-10 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="bulk-status-upload-btn"
                title="Upload filled template to bulk-update status"
              >
                <UploadIcon className="w-4 h-4 mr-1.5" /> Bulk Update Status
              </Button>
            </>
          )}
          <Dialog open={exportOpen} onOpenChange={setExportOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="rounded-none h-10 border-slate-300"
                data-testid="export-excel-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Export Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Export Invoice Status</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-1">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Status</Label>
                  <Select value={exportStatus} onValueChange={setExportStatus}>
                    <SelectTrigger data-testid="export-status" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {filters.status.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Party / Client</Label>
                  <Select value={exportParty} onValueChange={setExportParty}>
                    <SelectTrigger data-testid="export-party" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Parties</SelectItem>
                      {filters.party_client_name.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {exportCount !== null && (
                  <div
                    className={`text-xs px-3 py-2 border ${
                      exportCount.count > exportCount.max
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                    }`}
                    data-testid="export-count-info"
                  >
                    {exportCount.count === 0
                      ? "No invoices match these filters."
                      : exportCount.count > exportCount.max
                      ? exportStatus === "all"
                        ? `${exportCount.count} rows match. Maximum allowed per download is ${exportCount.max}. Please choose a specific Status to narrow the export.`
                        : `${exportCount.count} rows match. Maximum allowed per download is ${exportCount.max}. Please add a Party filter to narrow the export.`
                      : `${exportCount.count} of max ${exportCount.max} rows will be exported.`}
                  </div>
                )}
                <div className="text-[10px] text-slate-400">
                  Columns: SN, IN NO, RO No, Service Period, Project, SES Date, IN Amount, Status. Maximum 1000 rows per download.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setExportOpen(false)} className="rounded-none">Cancel</Button>
                <Button
                  onClick={exportExcel}
                  disabled={exportLoading || !exportCount || exportCount.count === 0 || exportCount.count > exportCount.max}
                  className="bg-[#002FA7] hover:bg-[#00227A] rounded-none"
                  data-testid="export-download-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {exportLoading ? "Downloading…" : "Download"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-invoice-btn"
                className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-10"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-display">Create Invoice</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                {[
                  ["invoice_number", "Invoice Number *", "text"],
                  ["fy", "FY", "text"],
                  ["party_client_name", "Party Client Name", "text"],
                  ["subvendor_name", "Subvendor Name", "text"],
                  ["rc_number", "RC Number", "text"],
                  ["rc_amount", "RC Amount", "number"],
                  ["rc_start_date", "RC Start Date", "date"],
                  ["rc_end_date", "RC End Date", "date"],
                  ["ro_no", "RO No", "text"],
                  ["ro_date", "RO Date", "date"],
                  ["invoice_date", "Invoice Date", "date"],
                  ["invoice_service_period", "Service Period", "text"],
                  ["base_amount", "Base Amount", "number"],
                  ["gst_amount", "GST Amount", "number"],
                  ["net_amount", "Net Amount", "number"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</Label>
                    <Input
                      type={type}
                      value={form[k] || ""}
                      onChange={(e) => updateField(k, e.target.value)}
                      data-testid={`new-invoice-${k}`}
                      className="rounded-none mt-1"
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Project *</Label>
                  <Select value={form.project || ""} onValueChange={(v) => updateField("project", v)}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="new-invoice-project">
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectList.filter(p => p.active).map((p) => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-400 mt-1">Project must be defined under Master Data &gt; Projects.</div>
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Description</Label>
                  <Input
                    value={form.invoice_description}
                    onChange={(e) => updateField("invoice_description", e.target.value)}
                    className="rounded-none mt-1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submitNew} data-testid="submit-invoice-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ---------- Bulk Status Update Dialog ---------- */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <UploadIcon className="w-5 h-5 text-amber-600" /> Bulk Update Status & Jobsheet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
              Step 1: Download the <strong>Status & Jobsheet Template</strong> (empty 8-column file). Step 2: Enter the Invoice # and any combination of fields you want to update — Status, Jobsheet Number, Jobsheet Upload Date, Jobsheet Final Approval Date, Invoice Upload Date, SES Approval Date and an optional manual Payment Due Date override. Step 3: Upload here — preview first, then apply.
              <div className="text-[10px] text-amber-700 mt-1">Tip: Leave <em>Payment Due Date</em> blank to let it auto-compute as SES Approval Date + 30 days. Dates accept DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, or native Excel dates.</div>
            </div>

            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Excel file (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                className="rounded-none mt-1"
                data-testid="bulk-status-file"
              />
              {bulkFile && (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="bulk-status-file-name">{bulkFile.name} · {(bulkFile.size / 1024).toFixed(1)} KB</div>
              )}
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={bulkDryRun}
                onChange={(e) => setBulkDryRun(e.target.checked)}
                data-testid="bulk-status-dry-run"
                className="accent-[#002FA7]"
              />
              <span>Dry-run · preview changes without saving (recommended)</span>
            </label>

            {bulkResult && (
              <div className="border border-slate-200 max-h-72 overflow-auto" data-testid="bulk-status-result">
                <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs flex-wrap">
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> {bulkResult.updated_count} will update</span>
                  <span className="text-slate-500">· {bulkResult.skipped_empty} blank-skipped · {bulkResult.skipped_same} no-change</span>
                  {bulkResult.error_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-700 font-semibold ml-auto"><AlertTriangle className="w-3.5 h-3.5" /> {bulkResult.error_count} error(s)</span>
                  )}
                  {bulkResult.dry_run && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5">Preview</span>
                  )}
                </div>
                {bulkResult.updated.length > 0 && (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 w-44">Invoice #</th>
                        <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.updated.map((u, idx) => (
                        <tr key={idx} className="border-b border-slate-100 align-top" data-testid={`bulk-status-row-${u.invoice_number}`}>
                          <td className="px-3 py-1.5 font-mono-data text-slate-900 align-top">{u.invoice_number}</td>
                          <td className="px-3 py-1.5">
                            <div className="space-y-0.5">
                              {Object.entries(u.changes || {}).map(([field, diff]) => (
                                <div key={field} className="flex gap-2 items-baseline">
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400 w-44 shrink-0">{field.replace(/_/g, " ")}</span>
                                  <span className="text-emerald-700 font-medium">{diff}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {bulkResult.errors.length > 0 && (
                  <div className="border-t border-slate-200">
                    <div className="px-3 py-1.5 bg-red-50 text-red-800 text-[10px] font-semibold uppercase tracking-wider">Errors</div>
                    <table className="w-full text-xs">
                      <thead className="bg-red-50/60">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-700 w-16">Row</th>
                          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-700">Invoice #</th>
                          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-700">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResult.errors.map((e, idx) => (
                          <tr key={idx} className="border-b border-red-100">
                            <td className="px-3 py-1.5 text-slate-500 font-mono-data">{e.row}</td>
                            <td className="px-3 py-1.5 font-mono-data text-slate-700">{e.invoice_number || "—"}</td>
                            <td className="px-3 py-1.5 text-red-700">{e.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="rounded-none">Close</Button>
            <Button
              onClick={runBulkUpload}
              disabled={bulkBusy || !bulkFile}
              className={`rounded-none ${bulkDryRun ? "bg-indigo-600 hover:bg-indigo-700" : "bg-amber-600 hover:bg-amber-700"}`}
              data-testid="bulk-status-submit"
            >
              {bulkBusy ? "Processing…" : (bulkDryRun ? "Preview Changes" : "Apply Changes")}
            </Button>
            {bulkResult && bulkResult.dry_run && (bulkResult.updated_count || 0) > 0 && (
              <Button
                onClick={applyAfterPreview}
                disabled={bulkBusy}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700"
                data-testid="bulk-status-apply"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Apply {bulkResult.updated_count} Change{bulkResult.updated_count === 1 ? "" : "s"} Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Type to search live · Invoice #, RO No, subvendor, project…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKey}
              data-testid="invoice-search"
              className="rounded-none pl-9 pr-9"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-700 text-lg leading-none"
                data-testid="invoice-search-clear"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="w-48">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="rounded-none mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="__unset__">(Unset · Newly uploaded)</SelectItem>
              {filters.status.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">FY</Label>
          <Select value={fyFilter} onValueChange={setFyFilter}>
            <SelectTrigger data-testid="filter-fy" className="rounded-none mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All FY</SelectItem>
              {filters.fy.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Party / Client</Label>
          <Select value={partyFilter} onValueChange={setPartyFilter}>
            <SelectTrigger data-testid="filter-party" className="rounded-none mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Parties</SelectItem>
              {filters.party_client_name.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Project</Label>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger data-testid="filter-project" className="rounded-none mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {filters.project.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pb-1">
          <button
            onClick={() => setSesPendingOnly((v) => !v)}
            className={`text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 h-10 border transition-colors ${
              sesPendingOnly
                ? "bg-amber-100 text-amber-800 border-amber-300 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-700"
            }`}
            data-testid="toggle-ses-pending"
            title="Show only invoices where SES is still pending"
          >
            {sesPendingOnly ? "✓ " : ""}SES Pending
          </button>
          {(outstandingOnly || overdueOnly || sesPendingOnly || statusFilter !== "all" || fyFilter !== "all" || partyFilter !== "all" || projectFilter !== "all") && (
            <button
              onClick={() => { setOutstandingOnly(false); setOverdueOnly(false); setSesPendingOnly(false); setStatusFilter("all"); setFyFilter("all"); setPartyFilter("all"); setProjectFilter("all"); }}
              className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-rose-700 border border-slate-200 px-2 py-1.5 h-10"
              data-testid="reset-filters"
            >
              Clear
            </button>
          )}
        </div>
        <Button onClick={load} variant="outline" className="rounded-none h-10" data-testid="apply-filters">Apply</Button>
      </div>

      {(outstandingOnly || overdueOnly || sesPendingOnly) && (
        <div className="flex gap-2 mb-4">
          {outstandingOnly && (
            <span className="chip-soft info inline-flex items-center gap-1.5" data-testid="chip-outstanding">
              Outstanding only
              <button onClick={() => setOutstandingOnly(false)} className="hover:text-rose-700">×</button>
            </span>
          )}
          {overdueOnly && (
            <span className="chip-soft inline-flex items-center gap-1.5 bg-red-50 text-red-700 border-red-200" data-testid="chip-overdue">
              Overdue only
              <button onClick={() => setOverdueOnly(false)} className="hover:text-rose-700">×</button>
            </span>
          )}
          {sesPendingOnly && (
            <span className="chip-soft inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 border-amber-200" data-testid="chip-ses-pending">
              SES Pending only
              <button onClick={() => setSesPendingOnly(false)} className="hover:text-rose-700">×</button>
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Party</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">FY · Project</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">RO No</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500" style={{ maxWidth: "220px" }}>Description</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Service Period</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Net Payable</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Outstanding</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">SES Date</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Due Date</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-36">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayedInvoices.length === 0 && (
              <tr><td colSpan={11} className="text-center text-sm text-slate-400 py-16">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No invoices found. Create one or upload via Excel.
              </td></tr>
            )}
            {pagedInvoices.map((inv) => {
              const sesDate = inv.jobsheet?.ses_approval_date;
              const dueDate = inv.payment_due_date;
              const today = new Date().toISOString().slice(0, 10);
              const isOverdue = dueDate && dueDate < today && (inv.outstanding || 0) > 0;
              return (
              <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-4 py-3">
                  <Link to={`/invoices/${inv.id}`} className="font-medium text-slate-900 hover:text-[#002FA7]" data-testid={`invoice-link-${inv.invoice_number}`}>
                    {inv.invoice_number}
                  </Link>
                  <div className="text-[10px] text-slate-400">{formatDate(inv.invoice_date)}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 font-medium">{inv.party_client_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{inv.fy || "—"}</div>
                  <div className="text-[10px] text-slate-400">{inv.project || ""}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {inv.ro_no ? (
                    <>
                      <div className="text-xs font-medium">{inv.ro_no}</div>
                      {inv.ro_date && <div className="text-[10px] text-slate-400">{formatDate(inv.ro_date)}</div>}
                    </>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-700" style={{ maxWidth: "220px" }}>
                  {inv.invoice_description ? (
                    <span
                      className="text-xs block"
                      title={inv.invoice_description}
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {inv.invoice_description}
                    </span>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-700 text-xs">{inv.invoice_service_period || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-data font-semibold text-slate-900">{formatINR(inv.net_payable)}</td>
                <td className={`px-4 py-3 text-right font-mono-data ${inv.outstanding > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
                  {formatINR(inv.outstanding)}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {sesDate ? (
                    <span className="text-xs">{formatDate(sesDate)}</span>
                  ) : (
                    <span className="status-pill bg-slate-100 text-slate-500 border-slate-200 text-[10px]">SES Pending</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {dueDate ? (
                    <span className={`text-xs ${isOverdue ? "text-red-700 font-semibold" : "text-slate-700"}`}>
                      {formatDate(dueDate)}
                      {isOverdue && <span className="ml-1.5 status-pill bg-red-50 text-red-700 border-red-200 text-[10px]">Overdue</span>}
                      {inv.payment_due_is_override && <span className="ml-1 text-[10px] text-blue-600" title="Manual override">✎</span>}
                    </span>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 w-36 align-top">
                  {inv.status ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium leading-snug border whitespace-normal break-words ${STATUS_TONE(inv.status)}`} style={{ maxWidth: "130px" }}>
                      {inv.status}
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium leading-snug border border-dashed bg-slate-50 text-slate-500 border-slate-300" data-testid={`status-unset-${inv.invoice_number}`}>
                      Unset
                    </span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={invPage} setPage={setInvPage}
          pageSize={invPageSize} setPageSize={setInvPageSize}
          totalPages={invTotalPages} totalItems={invTotalItems}
          testId="invoices-pagination"
        />
      </div>
    </div>
  );
}
